// apps/web/src/features/onboarding/pages/Step4_PaymentMethod.tsx
import { FormProvider, useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";

import {
  OnboardingStepLayout,
  AtlanteRadio,
  CheckboxField,
  TextInput,
} from "./../../../shared/ui/index";

import { HelpTooltip } from "../../../shared/ui/help/HelpTooltip";
import { Step4Schema, type Step4Input } from "../../../shared/schemas/step4";
import { useTranslation } from "react-i18next";

export type Step4PaymentFormValues = Step4Input;

const asset = (p: string) =>
  `${import.meta.env.BASE_URL}${encodeURI(p.replace(/^\/+/, ""))}`;

const CARD_TYPES = [
  { value: "visa", icon: asset("payments/visa.svg") },
  { value: "mastercard", icon: asset("payments/mastercard.svg") },
  { value: "applepay", icon: asset("payments/applepay.svg") },
] as const;

type Step4PaymentProps = {
  currentStep: number;
  totalSteps: number;
  initialValues?: Step4PaymentFormValues;
  onCompleted: (data: Step4PaymentFormValues) => void;
  onBack: () => void;
};

export default function Step4_PaymentMethod({
  currentStep,
  totalSteps,
  initialValues,
  onCompleted,
  onBack,
}: Step4PaymentProps) {
  const methods = useForm<Step4PaymentFormValues>({
    resolver: zodResolver(Step4Schema),
    defaultValues: initialValues ?? {
      invoicingFrequency: "weekly",
      paymentMethod: "card",
      sepaAuthorization: false,
      accountHolderName: "",
      iban: "",
    },
  });

  const { handleSubmit, control, watch } = methods;
  const { t } = useTranslation();

  const paymentMethod = watch("paymentMethod");

  const onSubmit = handleSubmit((data) => {
    sessionStorage.setItem("step4", JSON.stringify(data));
    onCompleted(data);
  });

  const translateError = (message?: string) =>
    message ? t(message, { defaultValue: message }) : "";

  return (
    <FormProvider {...methods}>
      <OnboardingStepLayout
        step={currentStep}
        totalSteps={totalSteps}
        title={t("onboarding.step3.title")}
        subtitle=""
        onNext={onSubmit}
        nextLabel={t("onboarding.step3.actions.next")}
        onBack={onBack}
        backLabel={t("onboarding.step3.actions.back")}
      >
        <Box sx={{ maxWidth: 520 }}>
          <Stack spacing={4}>
            <Controller
              name="invoicingFrequency"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#1A1A1A",
                      mb: 1,
                    }}
                  >
                    {t("onboarding.step3.frequency.label")}{" "}
                    <Typography
                      component="span"
                      sx={{ color: "#E63946", fontWeight: 700 }}
                    >
                      *
                    </Typography>
                  </Typography>

                  <RadioGroup
                    row
                    {...field}
                    sx={{ gap: 4, alignItems: "center" }}
                  >
                    <FormControlLabel
                      sx={{ marginLeft: 0 }}
                      value="weekly"
                      control={<AtlanteRadio />}
                      label={
                        <Typography sx={{ fontSize: "0.9rem", color: "#2D3748" }}>
                          {t("onboarding.step3.frequency.weekly")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      sx={{ marginLeft: 0 }}
                      value="monthly"
                      control={<AtlanteRadio />}
                      label={
                        <Typography sx={{ fontSize: "0.9rem", color: "#2D3748" }}>
                          {t("onboarding.step3.frequency.monthly")}
                        </Typography>
                      }
                    />
                  </RadioGroup>

                  {fieldState.error && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      {translateError(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />

            <Controller
              name="paymentMethod"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#1A1A1A",
                      mb: 1,
                    }}
                  >
                    {t("onboarding.step3.paymentMethod.label")}{" "}
                    <Typography
                      component="span"
                      sx={{ color: "#E63946", fontWeight: 700 }}
                    >
                      *
                    </Typography>
                  </Typography>

                  <RadioGroup
                    row
                    {...field}
                    sx={{ gap: 4, alignItems: "center" }}
                  >
                    <FormControlLabel
                      sx={{ marginLeft: 0 }}
                      value="card"
                      control={<AtlanteRadio />}
                      label={
                        <Typography sx={{ fontSize: "0.9rem", color: "#2D3748" }}>
                          {t("onboarding.step3.paymentMethod.options.card")}
                        </Typography>
                      }
                    />

                    <FormControlLabel
                      sx={{ marginLeft: 0 }}
                      value="sepa"
                      control={<AtlanteRadio />}
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography sx={{ fontSize: "0.9rem", color: "#2D3748" }}>
                            {t("onboarding.step3.paymentMethod.options.sepa")}
                          </Typography>

                          <HelpTooltip
                            title={t(
                              "onboarding.step3.paymentMethod.options.sepaTooltip"
                            )}
                            variant="question"
                          >
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                bgcolor: "#d7e2eb",
                                color: "#1A1A1A",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                fontWeight: 700,
                                userSelect: "none",
                                lineHeight: 1,
                              }}
                            >
                              ?
                            </Box>
                          </HelpTooltip>
                        </Stack>
                      }
                    />
                  </RadioGroup>

                  {fieldState.error && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                      {translateError(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />

            {paymentMethod === "card" && (
              <Controller
                name="cardType"
                control={control}
                render={({ field, fieldState }) => (
                  <Box sx={{ mt: -1 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#1A1A1A",
                        mb: 1,
                      }}
                    >
                      {t("onboarding.step3.cardType.label")}{" "}
                      <Typography
                        component="span"
                        sx={{ color: "#E63946", fontWeight: 700 }}
                      >
                        *
                      </Typography>
                    </Typography>

                    <RadioGroup
                      row
                      {...field}
                      value={field.value ?? ""}
                      sx={{ gap: 4, alignItems: "center" }}
                    >
                      {CARD_TYPES.map((card) => (
                        <FormControlLabel
                          key={card.value}
                          sx={{ marginLeft: 0 }}
                          value={card.value}
                          control={<AtlanteRadio />}
                          label={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.75}
                            >
                              <Box
                                component="img"
                                src={card.icon}
                                alt=""
                                sx={{
                                  width: 36,
                                  height: 24,
                                  objectFit: "contain",
                                  display: "block",
                                }}
                              />
                              <Typography
                                sx={{ fontSize: "0.9rem", color: "#2D3748" }}
                              >
                                {t(
                                  `onboarding.step3.cardType.options.${card.value}`
                                )}
                              </Typography>
                            </Stack>
                          }
                        />
                      ))}
                    </RadioGroup>

                    {fieldState.error && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {translateError(fieldState.error.message)}
                      </FormHelperText>
                    )}
                  </Box>
                )}
              />
            )}

            {paymentMethod === "sepa" && (
              <Stack spacing={2.5} sx={{ mt: -1 }}>
                <TextInput<Step4PaymentFormValues, "accountHolderName">
                  name="accountHolderName"
                  label={t("onboarding.step3.sepaFields.accountHolderName.label")}
                  placeholder={t(
                    "onboarding.step3.sepaFields.accountHolderName.placeholder"
                  )}
                  required
                />

                <TextInput<Step4PaymentFormValues, "iban">
                  name="iban"
                  label={t("onboarding.step3.sepaFields.iban.label")}
                  placeholder={t("onboarding.step3.sepaFields.iban.placeholder")}
                  required
                  inputProps={{
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />

                <Box sx={{ mt: 0 }}>
                  <div style={{ marginLeft: "12px" }}>
                    <CheckboxField<Step4PaymentFormValues, "sepaAuthorization">
                      name="sepaAuthorization"
                      label={
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            color: "#2D3748",
                            lineHeight: 1.35,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: "#F6C445",
                              fontWeight: 800,
                              mr: 0.5,
                            }}
                          ></Box>
                          {t("onboarding.step3.sepaAuthorization.label")}
                        </Typography>
                      }
                    />
                  </div>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </OnboardingStepLayout>
    </FormProvider>
  );
}
